import { TrendingItems } from '@algolia/recommend-react';
import recommend from '@algolia/recommend';
import { css, useTheme } from '@emotion/react';
import { Carousel } from '@mantine/carousel';
import React, { useMemo, memo } from 'react';
import { globals, ItemCard } from 'common-ui';
import { useRecoilValue } from 'recoil';
import { selectedStateAtom } from 'common-state';
import { useMediaQuery } from '@mantine/hooks';

type ListViewProps = {
  items: any[];
  itemComponent: any;
  isMiniCart: boolean;
  cmsCarousel: object;
};

type TrendingProductItemsProps = {
  isMiniCart?: boolean;
  showImageZoom?: boolean;
  wineType?: string[] | null;
  grape?: string[] | null;
  country?: string[] | null;
  productType?: string[] | null;
  awardWinner?: boolean;
  giftItems?: boolean;
  offerItems?: boolean;
  fineWine?: boolean;
  widgetTitle?: string;
  maxRecommendations?: number;
};

const ListView = React.memo(({ items, itemComponent, isMiniCart, cmsCarousel }: ListViewProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(`(max-width: ${theme.breakpointWidths?.sm}px)`);
  const isSm = useMediaQuery(`(max-width: ${theme.breakpointWidths?.md}px)`);
  const isMd = useMediaQuery(`(max-width: ${theme.breakpointWidths?.lg}px)`);
  const isLg = useMediaQuery(`(max-width: ${theme.breakpointWidths?.xl}px)`);

  let slidesToShow = 5;

  if (isXs) {
    slidesToShow = 1;
  } else if (isSm) {
    slidesToShow = 2;
  } else if (isMd) {
    slidesToShow = 3;
  } else if (isLg) {
    slidesToShow = 4;
  }

  const sliderSettings = [
    { maxWidth: theme.breakpointWidths?.xl, slideSize: '25%' },
    { maxWidth: theme.breakpointWidths?.lg, slideSize: '33.33333%' },
    { maxWidth: theme.breakpointWidths?.md, slideSize: '50%', slideGap: 'sm' },
    { maxWidth: theme.breakpointWidths?.sm, slideSize: '100%', slideGap: 0 },
  ];

  const tempSliderSetting = isMiniCart ? [] : sliderSettings;
  const showControls = Array.isArray(items) && items.length > (isMiniCart ? 1 : slidesToShow);

  return items.length > 0 ? (
    <Carousel
      withIndicators={showControls}
      withControls={showControls}
      loop={showControls}
      draggable={showControls}
      slidesToScroll={1}
      slideSize={isMiniCart ? '100%' : '20%'}
      align="start"
      slideGap="md"
      controlsOffset="md"
      className={`trending-products-carousel ${isMiniCart ? 'miniCart' : ''}`}
      styles={cmsCarousel}
      breakpoints={tempSliderSetting}
    >
      {items.map(item => (
        <Carousel.Slide key={item.objectID}>{React.createElement(itemComponent, { item })}</Carousel.Slide>
      ))}
    </Carousel>
  ) : null;
});

const TrendingProductItem = React.memo(
  ({ item, isMiniCart, showImageZoom }: { item: any; isMiniCart: boolean; showImageZoom: boolean }) => (
    <ItemCard product={item} showRating={!isMiniCart} showImageZoom={showImageZoom} />
  ),
);

const HeaderComponent = ({ widgetTitle = '' }: { widgetTitle?: string }) => (
  <div className="text-center">
    <h3 className="auc-Recommend-title">{widgetTitle}</h3>
  </div>
);
export function getTrendingBaseFilters(currentState?: string): string {
  if (currentState === undefined || currentState === null) {
    return 'inStock:true';
  }

  if (globals.country === 'us') {
    return `inStock:true AND vineyardPartner:false AND (threeTierStates:${currentState} OR wineryDirectStates:${currentState})`;
  }
  if (globals.country === 'au') {
    return 'inStock:true AND vineyardPartner:false';
  }
  return 'inStock:true';
}

export const TrendingProductItems = memo(
  ({
    isMiniCart = false,
    showImageZoom = true,
    wineType = null,
    grape = null,
    country = null,
    productType = null,
    awardWinner = false,
    giftItems = false,
    offerItems = false,
    fineWine = false,
    widgetTitle = '',
    maxRecommendations = 5,
  }: TrendingProductItemsProps) => {
    const recommendClient = useMemo(() => recommend(globals.algoliaAppId, globals.algoliaSearchKey), []);
    const indexName = globals.algoliaIndexName;

    const theme = useTheme();
    const productItemTrending = useMemo(
      () => css`
        ${theme.widgets.Cart.productItemsTrending}
      `,
      [theme],
    );

    const createFilterString = (field: string, values: Array<string>) => {
      const filterString = values.map(value => `${field}:'${value}'`).join(` OR `);
      const filterPart = values.length > 1 ? `(${filterString})` : filterString;
      return ` AND ${filterPart}`;
    };

    const currentState = useRecoilValue(selectedStateAtom);

    // Set the default filters for trending items
    let filters = getTrendingBaseFilters(currentState);

    if (wineType?.length) {
      filters += createFilterString('wineType', wineType);
    }
    if (country?.length) {
      filters += createFilterString('country', country);
    }
    if (grape?.length) {
      filters += createFilterString('grape', grape);
    }
    if (productType?.length) {
      const productTypeFilter = productType.map(type =>
        type.replace('Cases', 'Case').replace('Single Bottles', 'Wine'),
      );
      filters += createFilterString('productType', productTypeFilter);
    }
    if (awardWinner) {
      filters += ` AND awardWinner:${awardWinner}`;
    }
    if (giftItems) {
      filters += ` AND giftFlag:${giftItems}`;
    }
    if (offerItems) {
      filters += ` AND offers:'On Offer'`;
    }
    if (fineWine) {
      filters += ` AND fineWine:${fineWine}`;
    }

    const itemRenderer = (props: any) => (
      <TrendingProductItem item={props.item} isMiniCart={isMiniCart} showImageZoom={showImageZoom} />
    );

    const headerRenderer = (props: any) =>
      props.recommendations.length > 0 ? <HeaderComponent widgetTitle={widgetTitle} /> : <span></span>;

    const listViewRenderer = (props: any) => (
      <ListView {...props} isMiniCart={isMiniCart} cmsCarousel={theme.widgets.CmsCarousel} />
    );

    return (
      <div
        className="trending-items-container"
        data-testid="trending-items"
        css={productItemTrending}
        style={{ position: 'relative' }}
      >
        <TrendingItems
          recommendClient={recommendClient}
          indexName={indexName}
          queryParameters={{
            filters,
          }}
          itemComponent={itemRenderer}
          view={listViewRenderer}
          maxRecommendations={maxRecommendations || 5}
          headerComponent={headerRenderer}
        />
      </div>
    );
  },
);
